import type {
  ModuleDefinition,
  ModuleTier,
  SpecialBehaviorId,
} from "./phase-three-types";

export interface SkillRegradePatch
  extends Partial<
    Omit<
      ModuleDefinition,
      | "id"
      | "tier"
      | "hidden"
      | "special"
      | "evolutionTo"
      | "evolutionFrom"
    >
  > {
  id: string;
  tier?: ModuleTier;
  hidden?: boolean | null;
  special?: SpecialBehaviorId | null;
  evolutionTo?: string | null;
  evolutionFrom?: string | null;
}

export interface SkillRegradeDocument {
  version: 1;
  additions: ModuleDefinition[];
  patches: SkillRegradePatch[];
}

export function applySkillRegrades(
  modules: readonly ModuleDefinition[],
  document: SkillRegradeDocument,
): ModuleDefinition[] {
  const errors = validateSkillRegradeDocument(modules, document);
  if (errors.length > 0) {
    throw new Error(
      `技能迁阶配置无效：\n${errors
        .map((message) => `- ${message}`)
        .join("\n")}`,
    );
  }

  const additionById = new Map(
    document.additions.map((module) => [module.id, cloneModule(module)]),
  );
  const sourceModules = modules.map(
    (module) => additionById.get(module.id) ?? cloneModule(module),
  );
  for (const addition of document.additions) {
    if (!sourceModules.some((module) => module.id === addition.id)) {
      sourceModules.push(cloneModule(addition));
    }
  }

  const patchById = new Map(
    document.patches.map((patch) => [patch.id, patch]),
  );
  const patched = sourceModules.map((module) => {
    const patch = patchById.get(module.id);
    return patch ? applyPatch(module, patch) : module;
  });
  const relationshipErrors = validateEvolutionRelationships(patched);
  if (relationshipErrors.length > 0) {
    throw new Error(
      `技能迁阶后的升格关系无效：\n${relationshipErrors
        .map((message) => `- ${message}`)
        .join("\n")}`,
    );
  }
  return patched;
}

export function getSkillRegradeAdditions(
  document: SkillRegradeDocument,
): ModuleDefinition[] {
  return document.additions.map(cloneModule);
}

export function validateSkillRegradeDocument(
  modules: readonly ModuleDefinition[],
  document: SkillRegradeDocument,
): string[] {
  const errors: string[] = [];
  if (document.version !== 1) {
    errors.push("version 必须为 1。");
  }
  if (!Array.isArray(document.additions)) {
    errors.push("additions 必须是技能数组。");
  }
  if (!Array.isArray(document.patches)) {
    errors.push("patches 必须是迁阶补丁数组。");
  }
  if (errors.length > 0) {
    return errors;
  }

  const sourceIds = new Set(modules.map((module) => module.id));
  const additionIds = new Set<string>();
  for (const addition of document.additions) {
    if (!addition?.id) {
      errors.push("新增技能必须包含ID。");
      continue;
    }
    if (additionIds.has(addition.id)) {
      errors.push(`新增技能ID重复：${addition.id}。`);
    }
    additionIds.add(addition.id);
  }

  const availableIds = new Set([...sourceIds, ...additionIds]);
  const patchIds = new Set<string>();
  for (const patch of document.patches) {
    if (!patch?.id || !availableIds.has(patch.id)) {
      errors.push(`迁阶补丁目标不存在：${patch?.id ?? "(空ID)"}。`);
      continue;
    }
    if (patchIds.has(patch.id)) {
      errors.push(`迁阶补丁ID重复：${patch.id}。`);
    }
    patchIds.add(patch.id);
    if (
      patch.tier !== undefined &&
      (![1, 2, 3, 4, 5, 6].includes(patch.tier) ||
        !Number.isInteger(patch.tier))
    ) {
      errors.push(`技能 ${patch.id} 的目标品阶无效。`);
    }
  }
  return [...new Set(errors)];
}

function applyPatch(
  module: ModuleDefinition,
  patch: SkillRegradePatch,
): ModuleDefinition {
  const {
    id: _id,
    hidden,
    special,
    evolutionTo,
    evolutionFrom,
    ...fields
  } = patch;
  const result: ModuleDefinition = {
    ...cloneModule(module),
    ...fields,
    id: module.id,
    tags: fields.tags ? [...fields.tags] : [...module.tags],
    bonuses: fields.bonuses ? { ...fields.bonuses } : { ...module.bonuses },
    combat: fields.combat ? { ...fields.combat } : module.combat,
    economy: fields.economy ? { ...fields.economy } : module.economy,
    companion: fields.companion
      ? { ...fields.companion }
      : module.companion,
    growth: fields.growth ? { ...fields.growth } : module.growth,
    onAcquire: fields.onAcquire ? { ...fields.onAcquire } : module.onAcquire,
    healing: fields.healing ? { ...fields.healing } : module.healing,
    archaeology: fields.archaeology
      ? { ...fields.archaeology }
      : module.archaeology,
  };
  applyNullableField(result, "hidden", hidden);
  applyNullableField(result, "special", special);
  applyNullableField(result, "evolutionTo", evolutionTo);
  applyNullableField(result, "evolutionFrom", evolutionFrom);
  return result;
}

function applyNullableField<
  K extends "hidden" | "special" | "evolutionTo" | "evolutionFrom",
>(
  module: ModuleDefinition,
  key: K,
  value: ModuleDefinition[K] | null | undefined,
): void {
  if (value === null) {
    delete module[key];
  } else if (value !== undefined) {
    module[key] = value;
  }
}

function validateEvolutionRelationships(
  modules: readonly ModuleDefinition[],
): string[] {
  const errors: string[] = [];
  const byId = new Map(modules.map((module) => [module.id, module]));
  for (const module of modules) {
    if (module.tier === 5) {
      const ascended = module.evolutionTo
        ? byId.get(module.evolutionTo)
        : undefined;
      if (
        !ascended ||
        ascended.tier !== 6 ||
        ascended.evolutionFrom !== module.id
      ) {
        errors.push(`五阶技能 ${module.id} 缺少唯一对应的六阶升格。`);
      }
    } else if (module.evolutionTo) {
      errors.push(`非五阶技能 ${module.id} 不应包含 evolutionTo。`);
    }

    if (module.tier === 6) {
      const source = module.evolutionFrom
        ? byId.get(module.evolutionFrom)
        : undefined;
      if (
        !source ||
        source.tier !== 5 ||
        source.evolutionTo !== module.id
      ) {
        errors.push(`六阶技能 ${module.id} 缺少唯一对应的五阶来源。`);
      }
      if (module.weight !== 0) {
        errors.push(`六阶技能 ${module.id} 的随机权重必须为0。`);
      }
    } else if (module.evolutionFrom) {
      errors.push(`非六阶技能 ${module.id} 不应包含 evolutionFrom。`);
    }
  }
  return [...new Set(errors)];
}

function cloneModule(module: ModuleDefinition): ModuleDefinition {
  return {
    ...module,
    tags: [...module.tags],
    bonuses: { ...module.bonuses },
    combat: module.combat ? { ...module.combat } : undefined,
    economy: module.economy ? { ...module.economy } : undefined,
    companion: module.companion ? { ...module.companion } : undefined,
    growth: module.growth ? { ...module.growth } : undefined,
    onAcquire: module.onAcquire ? { ...module.onAcquire } : undefined,
    healing: module.healing ? { ...module.healing } : undefined,
    archaeology: module.archaeology
      ? { ...module.archaeology }
      : undefined,
  };
}
